import type { SubWalletResponse, TransferResponse } from "@developerz.ai/ggcoin-sdk";
import { createEffect, createSignal, For, Match, onMount, Show, Switch } from "solid-js";
import { useAuth } from "../auth/AuthContext.tsx";
import { ErrorBanner } from "../components/ErrorBanner.tsx";
import { Money } from "../components/Money.tsx";
import { parseAmountInput, sanitizeAmountInput } from "../lib/format.ts";
import { describeRejectReason } from "../lib/reject.ts";

// Match the server's WalletId rule (@ggcoin/core ids.ts): lowercase hex, UUID
// v7 (version nibble 7, variant 8-b). Keeping the client check in lockstep means
// a recipient id the hint accepts is one the api also accepts — no late 400 on
// an uppercase or non-v7 UUID the looser client regex used to wave through.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

/**
 * P2P transfer (#128): move coin from one of your sub-wallets to another user's
 * master wallet. Authorize → post happens server-side in one call; the result
 * is posted, require-approval (above your threshold), or reject.
 */
export function Send() {
  const { client, ensureMaster, masterWallet } = useAuth();
  // Live freeze gate (#153): if the master wallet is frozen (e.g. an operator
  // just froze it), block the send in the UI immediately — the cache updates
  // over the wallet WS channel, so this flips without a refresh.
  const frozen = () => masterWallet()?.status === "frozen";
  const [subs, setSubs] = createSignal<SubWalletResponse[]>([]);
  const [sourceSubWalletId, setSourceSubWalletId] = createSignal("");
  const [recipientWalletId, setRecipientWalletId] = createSignal("");
  const [amount, setAmount] = createSignal("");
  const [loading, setLoading] = createSignal(true);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<unknown | null>(null);
  const [result, setResult] = createSignal<TransferResponse | null>(null);

  // One idempotency key per transfer ATTEMPT: stable across retries of the same
  // inputs, regenerated when the inputs change or after a successful send.
  let idemKey = crypto.randomUUID();
  createEffect(() => {
    // Track the inputs so any change starts a fresh attempt key.
    sourceSubWalletId();
    recipientWalletId();
    amount();
    idemKey = crypto.randomUUID();
  });

  onMount(async () => {
    setLoading(true);
    setError(null);
    try {
      const master = await ensureMaster();
      const list = await client.listSubWallets(master.id);
      setSubs(list);
      setSourceSubWalletId((prev) => prev || list[0]?.id || "");
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  });

  async function onSend(e: Event) {
    e.preventDefault();
    setError(null);
    setResult(null);
    if (frozen()) {
      setError(new Error("Your wallet is frozen — sending is disabled."));
      return;
    }
    const parsed = parseAmountInput(amount());
    if (!parsed || BigInt(parsed) <= 0n) {
      setError(new Error("Enter an amount greater than zero (at most 2 decimals)."));
      return;
    }
    if (!sourceSubWalletId()) {
      setError(new Error("Pick a sub-wallet to send from."));
      return;
    }
    const recipient = recipientWalletId().trim();
    if (!recipient) {
      setError(new Error("Enter the recipient's master wallet id."));
      return;
    }
    if (!UUID_RE.test(recipient)) {
      setError(
        new Error(
          "That doesn't look like a wallet id. Paste the recipient's full wallet id (a UUID) from their Dashboard — not a short number.",
        ),
      );
      return;
    }
    setBusy(true);
    try {
      const res = await client.transfer({
        sourceSubWalletId: sourceSubWalletId(),
        recipientWalletId: recipient,
        amount: parsed,
        idempotencyKey: idemKey,
      });
      setResult(res);
      if (res.kind === "posted") {
        setAmount("");
        idemKey = crypto.randomUUID(); // a completed transfer is final
      }
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Show
      when={!loading()}
      fallback={
        <section class="card">
          <h2>Send</h2>
          <p class="muted">Loading your wallet…</p>
        </section>
      }
    >
      <section class="card">
        <h2>Send</h2>
        <p class="muted">
          Transfer coin to another user's master wallet — free, instant, closed-loop.
        </p>
        <ErrorBanner error={error()} />
        <Show when={frozen()}>
          <p class="reject-detail" role="alert">
            Your wallet is <strong>frozen</strong> — sending is disabled until it's unfrozen.
          </p>
        </Show>
        <Show
          when={subs().length > 0}
          fallback={<p>You need a sub-wallet to send from first. Create one under Sub-wallets.</p>}
        >
          <form onSubmit={onSend}>
            <label for="from">From sub-wallet</label>
            <select
              id="from"
              value={sourceSubWalletId()}
              onChange={(e) => setSourceSubWalletId(e.currentTarget.value)}
            >
              <For each={subs()}>
                {(s) => (
                  <option value={s.id}>
                    {s.label} ({s.id.slice(0, 8)}…)
                  </option>
                )}
              </For>
            </select>
            <label for="to" style={{ "margin-top": "0.75rem" }}>
              Recipient wallet id
            </label>
            <input
              id="to"
              value={recipientWalletId()}
              onInput={(e) => setRecipientWalletId(e.currentTarget.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              required
            />
            <p class="muted" style={{ margin: "0.25rem 0 0", "font-size": "0.8rem" }}>
              The recipient's full wallet id (a UUID) — they can copy it from their Dashboard.
            </p>
            <label for="amount" style={{ "margin-top": "0.75rem" }}>
              Amount (GG)
            </label>
            <input
              id="amount"
              inputmode="decimal"
              value={amount()}
              onInput={(e) => setAmount(sanitizeAmountInput(e.currentTarget.value))}
              placeholder="12.50"
              required
            />
            <div class="row" style={{ "margin-top": "0.75rem" }}>
              <button type="submit" class="primary" disabled={busy() || frozen()}>
                {busy() ? "Sending…" : "Send"}
              </button>
            </div>
          </form>
        </Show>
      </section>

      <Show when={result()}>
        {(r) => <ResultCard result={r()} onReset={() => setResult(null)} />}
      </Show>
    </Show>
  );
}

function ResultCard(props: { result: TransferResponse; onReset: () => void }) {
  return (
    <Switch>
      <Match when={props.result.kind === "posted" ? props.result : null}>
        {(posted) => (
          <section class="card">
            <h2>Sent ✅</h2>
            <p>
              <Money amount={posted().transaction.amount} /> sent to{" "}
              <span class="feed-id">{posted().transaction.toWalletId}</span>.
            </p>
            <p class="muted">
              Transaction <span class="feed-id">{posted().transaction.id}</span> ·{" "}
              {posted().transaction.status}
            </p>
            <button type="button" class="secondary" onClick={props.onReset}>
              New send
            </button>
          </section>
        )}
      </Match>
      <Match when={props.result.kind === "require-approval" ? props.result : null}>
        {(appr) => (
          <section class="card">
            <h2>Above threshold — approval needed</h2>
            <p>
              <Money amount={appr().amount} /> from{" "}
              <span class="feed-id">{appr().subWalletId}</span> needs master approval before it
              sends. It's now in the master's Approvals inbox.
            </p>
            <button type="button" class="secondary" onClick={props.onReset}>
              New send
            </button>
          </section>
        )}
      </Match>
      <Match when={props.result.kind === "reject" ? props.result : null}>
        {(rej) => <RejectCard reject={rej()} onReset={props.onReset} />}
      </Match>
    </Switch>
  );
}

function RejectCard(props: {
  reject: Extract<TransferResponse, { kind: "reject" }>;
  onReset: () => void;
}) {
  const copy = () => describeRejectReason(props.reject.reason);
  return (
    <section class="card reject-card" role="alert">
      <div class="reject-head">
        <span class="reject-icon" aria-hidden="true">
          ⛔
        </span>
        <h2>{copy().title}</h2>
      </div>
      <p class="reject-detail">
        {copy().detail}
        <Show when={copy().hint}>
          {" "}
          <strong>{copy().hint}</strong>
        </Show>
      </p>
      <p class="muted reject-code">
        <Money amount={props.reject.amount} /> · reason code <code>{props.reject.reason}</code>
      </p>
      <button type="button" class="secondary" onClick={props.onReset}>
        Try again
      </button>
    </section>
  );
}
